import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Switch, FormControlLabel,
  Alert, Chip
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, Star } from '@mui/icons-material';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';

const SEQUENCE_TYPES = [
  'CUSTOMER', 'SUPPLIER', 'SALES_ORDER', 'PURCHASE_ORDER',
  'SALES_INVOICE', 'PURCHASE_INVOICE', 'GOODS_RECEIPT', 'JOURNAL_ENTRY'
];

const DEFAULT_PREFIXES: Record<string, string> = {
  CUSTOMER: 'CUST-', SUPPLIER: 'SUPP-', SALES_ORDER: 'SO-', PURCHASE_ORDER: 'PO-',
  SALES_INVOICE: 'INV-', PURCHASE_INVOICE: 'BILL-', GOODS_RECEIPT: 'GR-', JOURNAL_ENTRY: 'JE-'
};

interface NumberSequence {
  id: number;
  sequenceType: string;
  prefix: string;
  suffix: string;
  nextNumber: number;
  padding: number;
  includeYear: boolean;
  includeMonth: boolean;
  isActive: boolean;
}

const emptyForm = {
  sequenceType: '', prefix: '', suffix: '', nextNumber: 1,
  padding: 5, includeYear: false, includeMonth: false, isActive: true
};

const NumberSequencesTab: React.FC = () => {
  const { t } = useLanguage();
  const [sequences, setSequences] = useState<NumberSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState('');

  useEffect(() => { loadSequences(); }, []);

  const loadSequences = async () => {
    try {
      const res = await settingsService.getNumberSequences();
      setSequences(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const usedTypes = sequences.map(s => s.sequenceType);
  const availableTypes = editId
    ? SEQUENCE_TYPES
    : SEQUENCE_TYPES.filter(t => !usedTypes.includes(t));

  const handleOpen = (seq?: NumberSequence) => {
    if (seq) {
      setEditId(seq.id);
      setForm({
        sequenceType: seq.sequenceType, prefix: seq.prefix, suffix: seq.suffix,
        nextNumber: seq.nextNumber, padding: seq.padding,
        includeYear: seq.includeYear, includeMonth: seq.includeMonth, isActive: seq.isActive
      });
    } else {
      setEditId(null);
      setForm({ ...emptyForm, sequenceType: availableTypes[0] || '' });
    }
    setPreview('');
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setEditId(null); };

  const computePreview = () => {
    const parts: string[] = [];
    if (form.prefix) parts.push(form.prefix);
    if (form.includeYear && form.includeMonth) {
      parts.push(new Date().getFullYear().toString() + String(new Date().getMonth() + 1).padStart(2, '0'));
    } else if (form.includeYear) {
      parts.push(new Date().getFullYear().toString());
    }
    parts.push(String(form.nextNumber).padStart(form.padding, '0'));
    if (form.suffix) parts.push(form.suffix);
    setPreview(parts.join(''));
  };

  useEffect(() => {
    if (dialogOpen) computePreview();
  }, [form.prefix, form.suffix, form.nextNumber, form.padding, form.includeYear, form.includeMonth, dialogOpen]);

  const handleSave = async () => {
    try {
      if (editId) {
        await settingsService.updateNumberSequence(editId, form);
      } else {
        await settingsService.createNumberSequence(form);
      }
      loadSequences();
      handleClose();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving sequence');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await settingsService.deleteNumberSequence(id);
      loadSequences();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const formatPreviewForRow = (seq: NumberSequence): string => {
    const parts: string[] = [];
    if (seq.prefix) parts.push(seq.prefix);
    if (seq.includeYear && seq.includeMonth) {
      parts.push(new Date().getFullYear().toString() + String(new Date().getMonth() + 1).padStart(2, '0'));
    } else if (seq.includeYear) {
      parts.push(new Date().getFullYear().toString());
    }
    parts.push(String(seq.nextNumber).padStart(seq.padding, '0'));
    if (seq.suffix) parts.push(seq.suffix);
    return parts.join('');
  };

  const columns: GridColDef[] = [
    {
      field: 'sequenceType', headerName: t('number_sequences.type'), width: 180,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={t(`number_sequences.types.${(p.value as string).toLowerCase()}`)} size="small" color="primary" variant="outlined" />
      )
    },
    { field: 'prefix', headerName: t('number_sequences.prefix'), width: 100 },
    {
      field: 'includeYear', headerName: t('number_sequences.year'), width: 80, align: 'center',
      renderCell: (p: GridRenderCellParams) => p.value ? '✓' : ''
    },
    {
      field: 'includeMonth', headerName: t('number_sequences.month'), width: 80, align: 'center',
      renderCell: (p: GridRenderCellParams) => p.value ? '✓' : ''
    },
    { field: 'padding', headerName: t('number_sequences.padding'), width: 80, align: 'center' },
    { field: 'nextNumber', headerName: t('number_sequences.next_number'), width: 110, align: 'right' },
    { field: 'suffix', headerName: t('number_sequences.suffix'), width: 100 },
    {
      field: 'preview', headerName: t('number_sequences.preview'), width: 220,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1565c0' }}>
          {formatPreviewForRow(p.row)}
        </Typography>
      )
    },
    {
      field: 'isActive', headerName: t('common.status'), width: 90,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.value ? t('common.active') : t('common.inactive')} color={p.value ? 'success' : 'default'} size="small" />
      )
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 100, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.edit')}>
            <IconButton size="small" onClick={() => handleOpen(p.row)}><Edit fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => handleDelete(p.row.id)}><Delete fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('number_sequences.title')}</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => handleOpen()}
          disabled={availableTypes.length === 0}>{t('number_sequences.new')}</Button>
      </Box>

      {sequences.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('number_sequences.empty')}</Alert>
      )}

      <DataGrid rows={sequences} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10]} disableRowSelectionOnClick />

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? t('number_sequences.edit') : t('number_sequences.new')}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label={t('number_sequences.type')} value={form.sequenceType}
            onChange={e => {
              const type = e.target.value;
              setForm({ ...form, sequenceType: type, prefix: DEFAULT_PREFIXES[type] || '' });
            }}
            disabled={!!editId} sx={{ mt: 1 }}>
            {(editId ? SEQUENCE_TYPES : availableTypes).map(type => (
              <MenuItem key={type} value={type}>{t(`number_sequences.types.${type.toLowerCase()}`)}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label={t('number_sequences.prefix')} value={form.prefix}
              onChange={e => setForm({ ...form, prefix: e.target.value })} />
            <TextField fullWidth label={t('number_sequences.suffix')} value={form.suffix}
              onChange={e => setForm({ ...form, suffix: e.target.value })} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth type="number" label={t('number_sequences.next_number')} value={form.nextNumber}
              onChange={e => setForm({ ...form, nextNumber: Math.max(1, Number(e.target.value)) })}
              inputProps={{ min: 1 }} />
            <TextField fullWidth type="number" label={t('number_sequences.padding')} value={form.padding}
              onChange={e => setForm({ ...form, padding: Math.max(1, Math.min(10, Number(e.target.value))) })}
              inputProps={{ min: 1, max: 10 }}
              helperText={t('number_sequences.padding_help')} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControlLabel control={<Switch checked={form.includeYear}
              onChange={e => setForm({ ...form, includeYear: e.target.checked, includeMonth: e.target.checked ? form.includeMonth : false })} />}
              label={t('number_sequences.include_year')} />
            <FormControlLabel control={<Switch checked={form.includeMonth} disabled={!form.includeYear}
              onChange={e => setForm({ ...form, includeMonth: e.target.checked })} />}
              label={t('number_sequences.include_month')} />
            <FormControlLabel control={<Switch checked={form.isActive}
              onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
              label={t('common.active')} />
          </Box>

          {preview && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<Star />}>
              {t('number_sequences.preview')}: <strong style={{ fontFamily: 'monospace' }}>{preview}</strong>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.sequenceType}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NumberSequencesTab;
